import React from "react";
import { LifeBuoy } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TIDS } from "@/lib/testIds";
import { EMAILS } from "@/lib/seo";

const faqs = [
  {
    q: "How long until I see new patients?",
    a: "Most clients see their first booked appointments from paid channels within 7–14 days of go-live. SEO compounds over 60–120 days. We show you a realistic timeline before we ever take you on.",
  },
  {
    q: "Do I need a big ad budget to work with you?",
    a: "No. Our smallest clients spend $1,500/mo on ads and still see profitable returns. What matters more is your show-up rate, offer, and speed-to-lead. Those are the three things we fix first.",
  },
  {
    q: "Are you an agency or a contractor?",
    a: "We're a specialist growth team, dentistry only. You get a dedicated strategist, a media buyer, a designer, and a full-stack developer, all working on your account, not a shared account manager.",
  },
  {
    q: "What if it doesn't work?",
    a: "We work in 90-day sprints with clear KPIs agreed upfront. If we miss them, we work for free until we hit them. Simple.",
  },
  {
    q: "Do you work with practices in my city?",
    a: "We only work with one practice per zip code / procedure niche. Book a call to check availability in your area before someone else does.",
  },
  {
    q: "What's included in the free strategy call?",
    a: "A full local competitor breakdown, a keyword & demand audit, a funnel review, and a realistic 90-day forecast for your practice, yours to keep whether we work together or not.",
  },
];

export default function Faq() {
  return (
    <section id="faq" className="py-24 md:py-32 relative">
      <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="font-mono-accent text-[11px] uppercase tracking-[0.25em] text-blue-400 mb-4">
            / 06 · Frequently asked
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-white leading-[1.05]">
            Straight answers.{" "}
            <span className="italic font-light text-white/60">No corporate spin.</span>
          </h2>

          <div className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
            <LifeBuoy className="w-5 h-5 text-amber-400" />
            <div className="mt-4 font-display text-lg text-white leading-snug">
              Still need a hand?
            </div>
            <p className="mt-2 text-sm text-white/55 leading-relaxed">
              Our team answers every question personally, usually within a few hours.
            </p>
            <a
              href={`mailto:${EMAILS.support}?subject=Question%20for%20Dental%20Leads%20Lab`}
              data-testid="faq-support-email-link"
              className="mt-4 inline-block text-sm text-amber-400 hover:text-amber-300 underline decoration-dotted underline-offset-4 transition-colors"
            >
              {EMAILS.support}
            </a>
          </div>
        </div>

        <div className="lg:col-span-8">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={TIDS.faqItem(i + 1)}
                className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl px-6 hover:border-white/20 transition-colors duration-200"
              >
                <AccordionTrigger className="font-display text-lg text-white text-left py-6 hover:no-underline hover:text-amber-400">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/65 pb-6 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
